/** Client intervals; responseHeadersMs includes browser scheduling, network and server wait. */
export type ApiRequestAttempt = {
  attempt: number;
  clientRequestId: string;
  serverRequestId: string | null;
  responseStatus: number | null;
  durationMs: number;
  responseHeadersMs: number | null;
  bodyReadMs: number | null;
  jsonParseMs: number | null;
  outcome: "SUCCESS" | "FAILURE";
  errorCode: string | null;
};
export type ApiRequestObserver = (attempt: Readonly<ApiRequestAttempt>) => void;

export function safeApiRequestId(value: string | null | undefined): string | null {
  return value && /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u.test(value) ? value : null;
}
function elapsed(startedAt: number) {
  return Math.min(60_000, Math.max(0, Math.round(performance.now() - startedAt)));
}
export class ApiAttemptMeasurement {
  readonly clientRequestId = crypto.randomUUID();
  private readonly startedAt = performance.now();
  private responseHeadersMs: number | null = null;
  private bodyReadMs: number | null = null;
  private jsonParseMs: number | null = null;
  private serverRequestId: string | null = null;
  private responseStatus: number | null = null;

  constructor(private readonly attempt: number, private readonly observer: ApiRequestObserver) {}
  received(response: Response) {
    this.responseHeadersMs = elapsed(this.startedAt);
    this.responseStatus = response.status;
    this.serverRequestId = safeApiRequestId(response.headers.get("x-request-id"));
  }
  async json(response: Response): Promise<unknown> {
    const readStartedAt = performance.now();
    const text = await response.text();
    this.bodyReadMs = elapsed(readStartedAt);
    const parseStartedAt = performance.now();
    try { return JSON.parse(text); }
    finally { this.jsonParseMs = elapsed(parseStartedAt); }
  }
  finish(outcome: ApiRequestAttempt["outcome"], errorCode: string | null) {
    try {
      this.observer(Object.freeze({
        attempt: this.attempt, clientRequestId: this.clientRequestId,
        serverRequestId: this.serverRequestId, responseStatus: this.responseStatus,
        durationMs: elapsed(this.startedAt), responseHeadersMs: this.responseHeadersMs,
        bodyReadMs: this.bodyReadMs, jsonParseMs: this.jsonParseMs, outcome, errorCode,
      }));
    } catch {
      // Observation must never fail or repeat the user's operation.
    }
  }
}

export function beginApiAttempt(attempt: number, observer?: ApiRequestObserver) {
  if (!observer) return undefined;
  try { return new ApiAttemptMeasurement(attempt, observer); }
  catch { return undefined; }
}
